import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

const quizQuestions = [
  {
    question: "What is the main focus of the space audit activity?",
    options: [
      "Rating environmental aspects and planning improvements",
      "Measuring room dimensions",
      "Counting furniture pieces"
    ],
    correctAnswer: 0,
    explanation: "The space audit involves selecting 5 daily environmental aspects, rating them 1-5, and noting one improvement for each to enhance your environmental well-being."
  },
  {
    question: "Which environmental factor most affects sleep quality?",
    options: [
      "Light and noise levels in the bedroom",
      "The color of bed sheets",
      "The brand of mattress"
    ],
    correctAnswer: 0,
    explanation: "Light and noise levels are critical environmental factors that affect sleep quality. Poor lighting can disrupt circadian rhythms, while noise pollution can prevent deep sleep and cause fatigue."
  },
  {
    question: "How does clutter in living spaces impact mental health?",
    options: [
      "It increases stress and reduces focus and productivity",
      "It makes rooms look smaller",
      "It reduces cleaning time"
    ],
    correctAnswer: 0,
    explanation: "Clutter has been shown to increase stress hormones, reduce concentration, and create feelings of overwhelm. A cluttered environment can make it harder to relax and focus on important tasks."
  },
  {
    question: "What role does indoor air quality play in daily well-being?",
    options: [
      "It affects cognitive function, mood, and respiratory health",
      "It determines room temperature",
      "It affects furniture durability"
    ],
    correctAnswer: 0,
    explanation: "Indoor air quality significantly impacts cognitive function, mood, and respiratory health. Poor air quality can cause fatigue, headaches, and reduced concentration."
  },
  {
    question: "Which aspect of home environment design promotes social connection?",
    options: [
      "Creating comfortable, inviting common spaces",
      "Using expensive furniture",
      "Having large storage areas"
    ],
    correctAnswer: 0,
    explanation: "Comfortable, inviting common spaces encourage social interaction and family bonding. The design of shared areas can either facilitate connection or create isolation."
  }
];

const EnvironmentalModule2QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('environmentalM2');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  useEffect(() => {
    if (responses[currentQuestion] !== undefined) {
      setSelectedAnswer(responses[currentQuestion]);
      setShowExplanation(true);
    } else {
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  }, [currentQuestion, responses]);

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    setShowExplanation(true);
    setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          Congratulations!
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 2 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              Key Achievements
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                The ecology of daily life
              </li>
              <li>
                Environmental stressors
              </li>
              <li>
                Safety and comfort foundations
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              Next Steps
            </h3>
            <p>Continue your environmental wellness journey by assessing your environmental quality of life!</p>
            <p>Ready to use the WHOQOL-BREF Environmental Domain to evaluate your surroundings?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/environmental-resources')}
          >
            Return to Environmental Resources
          </button>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left environmental-panel">
          <div className="module-header">
            <h1>Module 2 Quiz</h1>
            <p className="module-subtitle">The Ecology of Daily Life</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about environmental conditions and daily life.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/environmental-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalModule2QuizPage;

